package com.taocent.simple.compose.component.richtext.core.internal.platform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.abs

@Composable
actual fun rememberKeyboardHeight(): Dp {
    var keyboardHeightPx by remember { mutableStateOf(0.0) }

    DisposableEffect(Unit) {
        js("""
(function() {
    var d = window.__khData;
    if (d) { d.mountCount++; return; }
    if ((navigator.maxTouchPoints || 0) <= 0) return;

    var baselineH = window.innerHeight || 0;
    document.documentElement.style.height = baselineH + 'px';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.inset = '0';
    document.body.style.height = baselineH + 'px';
    document.body.style.overflow = 'hidden';
    window.scrollTo(0, 0);

    var listeners = [];

    function scrollHandler() { window.scrollTo(0, 0); }
    window.addEventListener('scroll', scrollHandler);
    listeners.push({t: window, type: 'scroll', fn: scrollHandler});

    var t = { kh: 0, lastRealKh: 0 };
    var lastOt = 0, lastOl = 0;

    function update() {
        var vv = window.visualViewport;
        var vvH = (vv && vv.height) || 0;
        var newKh = Math.max(0, baselineH - (vvH > 0 ? vvH : baselineH));
        if (newKh > 0) t.lastRealKh = newKh;
        t.kh = newKh;
        if (vv) {
            var ol = vv.offsetLeft || 0;
            var ot = vv.offsetTop || 0;
            if (ol !== lastOl || ot !== lastOt) {
                lastOl = ol; lastOt = ot;
                if (ol !== 0 || ot !== 0) {
                    document.documentElement.style.transform =
                        'translate(' + (-ol) + 'px,' + (-ot) + 'px)';
                } else {
                    document.documentElement.style.transform = '';
                }
            }
        }
    }

    function focusinHandler() {
        var vv = window.visualViewport;
        var vvH = (vv && vv.height) || 0;
        if (vvH > 0 && vvH < baselineH && t.lastRealKh > 0) {
            t.kh = t.lastRealKh;
        }
    }
    document.addEventListener('focusin', focusinHandler);
    listeners.push({t: document, type: 'focusin', fn: focusinHandler});

    function focusoutHandler() { t.kh = 0; }
    document.addEventListener('focusout', focusoutHandler);
    listeners.push({t: document, type: 'focusout', fn: focusoutHandler});

    window.addEventListener('resize', update);
    listeners.push({t: window, type: 'resize', fn: update});

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', update);
        window.visualViewport.addEventListener('scroll', update);
        listeners.push({t: window.visualViewport, type: 'resize', fn: update});
        listeners.push({t: window.visualViewport, type: 'scroll', fn: update});
    }

    var originalFocus = HTMLElement.prototype.focus;
    function patchedFocus(options) {
        options = options || {};
        if (options.preventScroll === undefined) options.preventScroll = true;
        originalFocus.call(this, options);
    }
    HTMLElement.prototype.focus = patchedFocus;

    window.__kh = t;
    window.__khData = {
        mountCount: 1,
        listeners: listeners,
        originalFocus: originalFocus,
        patchedFocus: patchedFocus,
    };

    update();
})();
""")
        onDispose {
            js("""
(function() {
    var d = window.__khData;
    if (!d) return;
    d.mountCount--;
    if (d.mountCount > 0) return;

    d.listeners.forEach(function(l) {
        try { l.t.removeEventListener(l.type, l.fn); } catch(e) {}
    });
    d.listeners = [];

    if (d.originalFocus && d.patchedFocus &&
        HTMLElement.prototype.focus === d.patchedFocus) {
        HTMLElement.prototype.focus = d.originalFocus;
    }

    document.body.style.position = '';
    document.body.style.inset = '';
    document.body.style.height = '';
    document.body.style.overflow = '';
    document.documentElement.style.height = '';
    document.documentElement.style.overflow = '';
    document.documentElement.style.transform = '';

    window.__kh = null;
    window.__khData = null;
})();
""")
        }
    }

    LaunchedEffect(Unit) {
        while (true) {
            val newPx = try {
                val v = js("window.__kh ? window.__kh.kh : 0")
                (v as Number).toDouble()
            } catch (_: Throwable) { keyboardHeightPx }
            if (abs(newPx - keyboardHeightPx) > 0.5) {
                keyboardHeightPx = newPx
            }
            withFrameNanos {}
        }
    }

    return keyboardHeightPx.toFloat().dp
}

@Composable
actual fun rememberKeyboardNeedsInsets(): Boolean {
    var isMobile by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        isMobile = try {
            val tp = js("navigator.maxTouchPoints")
            (tp as Number).toInt() > 0
        } catch (_: Throwable) { false }
        onDispose {}
    }

    return isMobile
}